import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, Marker, Polyline, Popup, CircleMarker, useMap } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useApiQuery } from "@/hooks/useApi";

const OSM_URL = "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const OSM_ATTR = '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';
// Bundle the default marker icons from the leaflet package (no runtime CDN,
// so the map works offline and under a strict CSP).
import markerIcon2x from "leaflet/dist/images/marker-icon-2x.png";
import markerIcon from "leaflet/dist/images/marker-icon.png";
import markerShadow from "leaflet/dist/images/marker-shadow.png";

// Fix Leaflet's default marker icon paths so they resolve through webpack.
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const navyIcon = L.divIcon({
  html: `<div style="background:#0E2F76;color:#fff;border-radius:50%;width:22px;height:22px;display:grid;place-items:center;box-shadow:0 0 0 3px #fff, 0 0 0 4px #0E2F76;font-family:'IBM Plex Sans';font-size:11px;font-weight:800">•</div>`,
  className: "vtx-marker",
  iconSize: [22, 22],
  iconAnchor: [11, 11],
});
const currentIcon = L.divIcon({
  html: `<div style="background:#d41f26;color:#fff;border-radius:50%;width:26px;height:26px;display:grid;place-items:center;box-shadow:0 0 0 3px #fff, 0 0 0 5px rgba(212,31,38,0.35);font-family:'IBM Plex Sans';font-size:12px;font-weight:800;animation:vtx-pulse 1.6s infinite ease-in-out">◉</div>
    <style>@keyframes vtx-pulse{0%,100%{transform:scale(1)}50%{transform:scale(1.15)}}</style>`,
  className: "vtx-marker-current",
  iconSize: [26, 26],
  iconAnchor: [13, 13],
});

// The map is non-critical chrome — never let a Leaflet error take down the whole
// shipment page. Contain any failure to the map area with a graceful fallback.
class MapErrorBoundary extends React.Component {
  constructor(props) { super(props); this.state = { failed: false }; }
  static getDerivedStateFromError() { return { failed: true }; }
  componentDidCatch(err) { /* eslint-disable-next-line no-console */ console.warn("Map render failed:", err); }
  render() {
    if (this.state.failed) {
      return (
        <div className="w-full border border-[#A9C0E0]/60 grid place-items-center text-center vtx-diagonal"
             style={{ height: this.props.height, borderRadius: 2 }} data-testid="map-error">
          <div>
            <div className="text-sm font-bold text-[#0E2F76]">Map unavailable</div>
            <div className="text-xs text-[#475569] mt-1 max-w-xs">The route map couldn&apos;t be displayed for this shipment, but the rest of the details are below.</div>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    // Only fit to points with real coordinates — a null lat/lon would crash
    // Leaflet's projection (setView/fitBounds).
    const pts = (points || []).filter(p => p && Number.isFinite(p.lat) && Number.isFinite(p.lon));
    if (pts.length === 0) return;
    if (pts.length === 1) {
      map.setView([pts[0].lat, pts[0].lon], 5);
    } else {
      const b = L.latLngBounds(pts.map(p => [p.lat, p.lon]));
      if (b.isValid()) map.fitBounds(b, { padding: [40, 40], maxZoom: 6 });
    }
  }, [points, map]);
  return null;
}

const MODE_STYLE = {
  air: { color: "#1D4ED8", dash: "2 8" },
  sea: { color: "#0E2F76", dash: "10 8" },
  road: { color: "#B45309", dash: null },
};

// --- Route simulation: interpolate a position along the polyline (mirrors
// backend simulate.py) so a vehicle marker can glide between milestones. ---
function jsHaversine(a, b) {
  const R = 6371, toRad = (x) => (x * Math.PI) / 180;
  const dLat = toRad(b[0] - a[0]), dLon = toRad(b[1] - a[1]);
  const s = Math.sin(dLat / 2) ** 2 + Math.cos(toRad(a[0])) * Math.cos(toRad(b[0])) * Math.sin(dLon / 2) ** 2;
  return 2 * R * Math.asin(Math.min(1, Math.sqrt(s)));
}
function pointAtFraction(latlngs, f) {
  if (!latlngs || latlngs.length === 0) return null;
  if (latlngs.length === 1) return latlngs[0];
  f = Math.max(0, Math.min(1, f));
  const seg = [];
  let total = 0;
  for (let i = 1; i < latlngs.length; i++) { const d = jsHaversine(latlngs[i - 1], latlngs[i]); seg.push(d); total += d; }
  if (total <= 0) return latlngs[0];
  const target = f * total;
  let acc = 0;
  for (let i = 0; i < seg.length; i++) {
    if (acc + seg[i] >= target || i === seg.length - 1) {
      const sf = seg[i] === 0 ? 0 : Math.max(0, Math.min(1, (target - acc) / seg[i]));
      return [latlngs[i][0] + (latlngs[i + 1][0] - latlngs[i][0]) * sf,
              latlngs[i][1] + (latlngs[i + 1][1] - latlngs[i][1]) * sf];
    }
    acc += seg[i];
  }
  return latlngs[latlngs.length - 1];
}

const VEHICLE_EMOJI = { air: "✈️", sea: "🚢", road: "🚚" };
function vehicleIcon(mode) {
  const e = VEHICLE_EMOJI[String(mode || "").toLowerCase()] || VEHICLE_EMOJI.road;
  return L.divIcon({
    html: `<div style="font-size:20px;line-height:1;filter:drop-shadow(0 1px 2px rgba(0,0,0,0.45));animation:vtx-float 1.8s ease-in-out infinite">${e}</div>
      <style>@keyframes vtx-float{0%,100%{transform:translateY(0)}50%{transform:translateY(-3px)}}</style>`,
    className: "vtx-vehicle", iconSize: [24, 24], iconAnchor: [12, 12],
  });
}

// Animated vehicle: eases from the route start to the current fraction on mount.
function SimulatedVehicle({ path, target, mode, eta, fraction }) {
  const [pos, setPos] = useState(() => pointAtFraction(path, 0));
  useEffect(() => {
    let raf;
    const dur = 2600, t0 = (typeof performance !== "undefined" ? performance.now() : Date.now());
    const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
    const tick = (now) => {
      const p = Math.min(1, (now - t0) / dur);
      setPos(pointAtFraction(path, ease(p) * target));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [path, target]);
  if (!pos) return null;
  return (
    <Marker position={pos} icon={vehicleIcon(mode)} zIndexOffset={1000} data-testid="sim-vehicle">
      <Popup>
        <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
          <div style={{ fontWeight: 700, color: "#0E2F76" }}>En route · {String(mode || "").toUpperCase()}</div>
          <div style={{ fontSize: 11, color: "#475569" }}>~{Math.round((fraction || 0) * 100)}% of the way</div>
          {eta && <div style={{ fontSize: 11, color: "#475569" }}>ETA {new Date(eta).toLocaleDateString()}</div>}
        </div>
      </Popup>
    </Marker>
  );
}

export default function TrackingMap({ route, loading, height = 320 }) {
  const points = route?.points || [];
  const current = route?.current;
  const ais = route?.ais;
  const segments = route?.segments || [];
  const simulated = route?.simulated;   // {lat, lon, fraction} while in transit
  const eta = route?.eta;
  const simMode = route?.segments?.find((s) => s.status === "IN_TRANSIT")?.mode
    || route?.segments?.[0]?.mode;
  // Admin-configurable tile layer (shared across map instances via the query cache).
  const { data: mapCfg } = useApiQuery("map-config", "/v1/public/map-config", { staleTime: 300000 });
  const tileUrl = mapCfg?.tiles?.url_template || OSM_URL;
  const tileAttr = mapCfg?.tiles?.attribution || OSM_ATTR;

  const polyline = useMemo(
    () => points.filter(p => Number.isFinite(p.lat) && Number.isFinite(p.lon)).map(p => [p.lat, p.lon]),
    [points]
  );
  const legLines = useMemo(
    () => segments
      .filter(s => s.from && s.to && Number.isFinite(s.from.lat) && Number.isFinite(s.to.lat)
        && Number.isFinite(s.from.lon) && Number.isFinite(s.to.lon))
      .map(s => ({
        positions: [[s.from.lat, s.from.lon], [s.to.lat, s.to.lon]],
        style: MODE_STYLE[String(s.mode || "").toLowerCase()] || MODE_STYLE.road,
        seq: s.sequence, mode: s.mode, status: s.status,
        label: `${s.from.label} → ${s.to.label}`,
      })),
    [segments]
  );

  // Only ever hand Leaflet coordinates that are real numbers. An older shipment
  // whose waypoints were never geocoded has null lat/lon, which would crash the
  // map's projection (setView/project) and take down the whole page.
  const validPoints = useMemo(
    () => points.filter(p => p && Number.isFinite(p.lat) && Number.isFinite(p.lon)),
    [points]
  );
  const validCurrent = current && Number.isFinite(current.lat) && Number.isFinite(current.lon) ? current : null;
  const validAis = ais && Number.isFinite(ais.lat) && Number.isFinite(ais.lon) ? ais : null;

  if (loading) {
    return (
      <div className="w-full vtx-skeleton" style={{ height, borderRadius: 2 }} data-testid="map-loading" />
    );
  }

  if (!route || (validPoints.length === 0 && !validCurrent && !validAis)) {
    return (
      <div className="w-full border border-[#A9C0E0]/60 grid place-items-center text-center vtx-diagonal"
           style={{ height, borderRadius: 2 }} data-testid="map-empty">
        <div>
          <div className="text-sm font-bold text-[#0E2F76]">Route unavailable</div>
          <div className="text-xs text-[#475569] mt-1 max-w-xs">We couldn&apos;t geocode any waypoints yet. The route will appear once events come in.</div>
        </div>
      </div>
    );
  }

  const center = validCurrent ? [validCurrent.lat, validCurrent.lon]
    : validAis ? [validAis.lat, validAis.lon]
    : [validPoints[0].lat, validPoints[0].lon];

  return (
   <MapErrorBoundary height={height}>
    <div className="w-full border border-[#A9C0E0]/60 overflow-hidden" style={{ borderRadius: 2, position: "relative" }} data-testid="tracking-map">
      {simulated && !validAis && (
        <div style={{ position: "absolute", top: 8, right: 8, zIndex: 1000, pointerEvents: "none" }}
             className="bg-white/95 border border-[#A9C0E0]/70 px-2.5 py-1 text-[10px] font-bold text-[#0E2F76] shadow-sm"
             data-testid="map-eta">
          En route{eta ? ` · ETA ${new Date(eta).toLocaleDateString()}` : ""}
        </div>
      )}
      <MapContainer center={center} zoom={4} style={{ height, width: "100%" }} scrollWheelZoom={false}>
        <TileLayer attribution={tileAttr} url={tileUrl} />
        <FitBounds points={validAis ? [...validPoints, validAis] : validPoints} />

        {legLines.length >= 1 ? (
          legLines.map((l, i) => (
            <Polyline
              key={i}
              positions={l.positions}
              pathOptions={{ color: l.style.color, weight: 3.5, opacity: l.status === "COMPLETED" ? 0.9 : 0.6, dashArray: l.style.dash || undefined }}
            >
              <Popup>
                <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                  <div style={{ fontWeight: 700, color: l.style.color }}>Leg {l.seq} · {String(l.mode || "").toUpperCase()}</div>
                  <div style={{ fontSize: 11, color: "#475569" }}>{l.label}</div>
                </div>
              </Popup>
            </Polyline>
          ))
        ) : (
          polyline.length >= 2 && (
            <Polyline positions={polyline} pathOptions={{ color: "#0E2F76", weight: 3, opacity: 0.85, dashArray: "6 6" }} />
          )
        )}

        {validPoints.map((p, i) => (
          <Marker key={i} position={[p.lat, p.lon]} icon={p.is_current && !validAis ? currentIcon : navyIcon}>
            <Popup>
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                <div style={{ fontWeight: 700, color: "#0E2F76" }}>{p.label}</div>
                <div style={{ fontSize: 11, color: "#475569" }}>{p.query}</div>
              </div>
            </Popup>
          </Marker>
        ))}

        {validCurrent && !validPoints.some(p => p.is_current) && (
          <Marker position={[validCurrent.lat, validCurrent.lon]} icon={currentIcon}>
            <Popup><b>Current</b><div style={{fontSize:11}}>{validCurrent.label}</div></Popup>
          </Marker>
        )}

        {simulated && !validAis && polyline.length >= 2 && (
          <SimulatedVehicle path={polyline} target={simulated.fraction ?? 0} mode={simMode} eta={eta} fraction={simulated.fraction} />
        )}

        {validAis && (
          <CircleMarker center={[validAis.lat, validAis.lon]} radius={9} pathOptions={{ color: "#d41f26", fillColor: "#d41f26", fillOpacity: 0.85 }}>
            <Popup>
              <div style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}>
                <div style={{ fontWeight: 700, color: "#d41f26" }}>Live AIS · {validAis.provider}</div>
                <div style={{ fontSize: 11 }}>MMSI {validAis.vessel_mmsi}</div>
                {validAis.speed != null && <div style={{ fontSize: 11 }}>Speed {validAis.speed} kt · Course {validAis.course || "—"}°</div>}
              </div>
            </Popup>
          </CircleMarker>
        )}
      </MapContainer>
    </div>
   </MapErrorBoundary>
  );
}
